import { Accordion, AccordionDetails, AccordionSummary, Button, Checkbox, Divider, FormControlLabel, FormGroup, Stack, TextField, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Form } from '../../StyledComponents/Input/generic';
import SaveIcon from '@mui/icons-material/Save';
import { toast } from 'react-toastify';
import AttToken from '../../helpers/attToken';
import { trackEventMatomo } from '../../helpers/matomo';
import Companies, { PrioritySLA, TicketNotificationEvent, TicketNotificationRecipient, TicketNotifications } from '../../service/Companies';
import { NOTIFICATION_EVENT_LABELS, NOTIFICATION_RECIPIENT_LABELS } from '../../helpers/ticketNotificationConfig';

const empresaService = new Companies();
const DAY_MS = 24 * 60 * 60 * 1000;
const HOUR_MS = 60 * 60 * 1000;
const MINUTE_MS = 60 * 1000;

const emptyPrioritySLA: PrioritySLA = {
  low: { firstAttendance: 0, startProgress: 0, completion: 0 },
  medium: { firstAttendance: 0, startProgress: 0, completion: 0 },
  high: { firstAttendance: 0, startProgress: 0, completion: 0 },
};

const notificationEvents: TicketNotificationEvent[] = ['opened', 'accepted', 'approved', 'rejected', 'inProgress', 'completed', 'canceled', 'closed', 'reopened', 'deadlineReached', 'priorityChanged', 'commented'];
const notificationRecipients: TicketNotificationRecipient[] = ['team', 'teamSupervisor', 'responsible', 'requester'];

const emptyTicketNotifications = (): TicketNotifications => ({
  events: {
    opened: [],
    accepted: [],
    approved: [],
    rejected: [],
    inProgress: [],
    completed: [],
    canceled: [],
    closed: [],
    reopened: [],
    deadlineReached: [],
    priorityChanged: [],
    commented: [],
  },
});

const normalizeTicketNotifications = (value: Partial<TicketNotifications> | null | undefined): TicketNotifications => ({
  events: notificationEvents.reduce((events, event) => {
    const configuredEvents = value?.events;
    const hasEvent = configuredEvents !== undefined && Object.prototype.hasOwnProperty.call(configuredEvents, event);

    return {
      ...events,
      [event]: hasEvent ? configuredEvents[event] || [] : ['team'],
    };
  }, {} as TicketNotifications['events']),
});

const priorityOptions = [
  { key: 'low', label: 'notifications.myTickets.low' },
  { key: 'medium', label: 'notifications.myTickets.medium' },
  { key: 'high', label: 'notifications.myTickets.high' },
] as const;

const slaOptions = [
  { key: 'firstAttendance', label: 'notifications.ticket.firstAttendance' },
  { key: 'startProgress', label: 'notifications.ticket.startProgress' },
  { key: 'completion', label: 'notifications.ticket.completion' },
] as const;

function DurationInput({ value, disabled, onChange }: { value: number; disabled: boolean; onChange: (value: number) => void }) {
  const { t } = useTranslation('translation');
  const [duration, setDuration] = useState({ day: '', hour: '', minute: '' });

  useEffect(() => {
    const safeValue = Number.isFinite(value) && value >= 0 ? value : 0;
    setDuration({
      day: Math.floor(safeValue / DAY_MS).toString(),
      hour: Math.floor((safeValue % DAY_MS) / HOUR_MS).toString(),
      minute: Math.floor((safeValue % HOUR_MS) / MINUTE_MS).toString(),
    });
  }, [value]);

  const update = (key: 'day' | 'hour' | 'minute', rawValue: string) => {
    if (rawValue !== '' && (!/^\d+$/.test(rawValue) || Number(rawValue) < 0)) return;
    const next = { ...duration, [key]: rawValue };
    const days = Number(next.day || 0);
    const hours = Number(next.hour || 0);
    const minutes = Number(next.minute || 0);
    const totalMinutes = days * 24 * 60 + hours * 60 + minutes;
    onChange(totalMinutes * MINUTE_MS);
  };

  return (
    <Stack direction="row" spacing={1} flex={1}>
      <TextField size="small" type="text" disabled={disabled} inputProps={{ inputMode: 'numeric', maxLength: 3, style: { textAlign: 'center' } }} label={t('formDetails.GeneralTab.dias')} value={duration.day} onChange={event => update('day', event.target.value)} />
      <TextField size="small" type="text" disabled={disabled} inputProps={{ inputMode: 'numeric', maxLength: 2, style: { textAlign: 'center' } }} label={t('hour')} value={duration.hour} onChange={event => update('hour', event.target.value)} />
      <TextField size="small" type="text" disabled={disabled} inputProps={{ inputMode: 'numeric', maxLength: 2, style: { textAlign: 'center' } }} label={t('minutos')} value={duration.minute} onChange={event => update('minute', event.target.value)} />
    </Stack>
  );
}

const NotificationConfig = () => {
  const { t } = useTranslation('translation');
  const [prioritySLA, setPrioritySLA] = useState<PrioritySLA | null>(null);
  const [ticketNotifications, setTicketNotifications] = useState<TicketNotifications>(emptyTicketNotifications);

  const { data: company, isLoading, isSuccess, isError } = useQuery({
    queryKey: ['GET_COMPANY_PRIORITY_SLA'],
    queryFn: async () => {
      const token = await AttToken();
      if (!token) throw new Error('Token not found');
      return empresaService.getById(token, token.userEmpresa);
    },
  });

  useEffect(() => {
    setPrioritySLA(company?.prioritySLA ?? null);
    setTicketNotifications(normalizeTicketNotifications(company?.ticketNotifications));
  }, [company]);

  useEffect(() => {
    if (isSuccess) trackEventMatomo('configurações de notificações', 'sucesso', 'carregamento', 'configurações carregadas');
    if (isError) trackEventMatomo('configurações de notificações', 'erro', 'carregamento', 'falha ao carregar configurações');
  }, [isError, isSuccess]);

  const { mutate: savePrioritySLA, isPending: isSavingSLA } = useMutation({
    mutationKey: ['CHANGE_COMPANY_PRIORITY_SLA'],
    mutationFn: async (nextPrioritySLA: PrioritySLA | null) => {
      const token = await AttToken();
      if (!token) throw new Error('Token not found');
      if (nextPrioritySLA && Object.values(nextPrioritySLA).some(priority => Object.values(priority).some(value => value <= 0))) {
        throw new Error(t('notifications.ticket.slaValidationError'));
      }
      const response = await empresaService.changePrioritySLA(token, nextPrioritySLA);
      if (!response || typeof response === 'number') throw new Error('Error saving priority SLA');
      return response.prioritySLA ?? null;
    },
    onSuccess: savedPrioritySLA => {
      trackEventMatomo('configurações de notificações', 'sucesso', 'salvamento', 'SLA padrão atualizado');
      setPrioritySLA(savedPrioritySLA);
    },
    onError: (error) => {
      toast.error(error.message, { toastId: 'change-company-priority-sla-error' });
      trackEventMatomo('configurações de notificações', 'erro', 'salvamento', 'falha ao atualizar SLA padrão');
    }
  });

  const { mutate: saveTicketNotifications, isPending: isSavingNotifications } = useMutation({
    mutationKey: ['CHANGE_TICKET_NOTIFICATIONS'],
    mutationFn: async (nextTicketNotifications: TicketNotifications) => {
      const token = await AttToken();
      if (!token) throw new Error('Token not found');
      const response = await empresaService.changeTicketNotifications(token, nextTicketNotifications);
      if (!response || typeof response === 'number') throw new Error('Error saving ticket notifications');
      return response.ticketNotifications as TicketNotifications;
    },
    onSuccess: savedTicketNotifications => {
      trackEventMatomo('configurações de notificações', 'sucesso', 'salvamento', 'destinatários de notificações atualizados');
      setTicketNotifications(normalizeTicketNotifications(savedTicketNotifications));
    },
    onError: () => trackEventMatomo('configurações de notificações', 'erro', 'salvamento', 'falha ao atualizar destinatários de notificações'),
  });

  const updatePrioritySLA = (priority: keyof PrioritySLA, sla: keyof PrioritySLA['low'], value: number) => {
    setPrioritySLA(current => current ? ({ ...current, [priority]: { ...current[priority], [sla]: value } }) : current);
  };

  const toggleRecipient = (event: TicketNotificationEvent, recipient: TicketNotificationRecipient, checked: boolean) => {
    setTicketNotifications(current => ({
      events: {
        ...current.events,
        [event]: checked
          ? [...current.events[event], recipient]
          : current.events[event].filter(value => value !== recipient),
      },
    }));
  };

  const isDisabled = isLoading || isSavingSLA || isSavingNotifications;

  return (
    <Form>
      <h1>{t('notifications.newModificationModal.notificationConfig')}</h1>
      <Typography color="textSecondary" fontWeight="bold" paddingTop={2}>{t('notifications.ticket.SLA')}</Typography>
      <FormControlLabel
        label="Configurar SLA padrão da empresa"
        control={<Checkbox disabled={isDisabled} checked={prioritySLA !== null} onChange={event => setPrioritySLA(event.target.checked ? emptyPrioritySLA : null)} />}
      />
      <Stack gap={1} paddingTop={2} paddingBottom={2}>
        {slaOptions.map(sla => (
          <Accordion key={sla.key} defaultExpanded={sla.key === 'firstAttendance'} disabled={isDisabled || prioritySLA === null}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls={`company-sla-${sla.key}-content`} id={`company-sla-${sla.key}-header`}>
              <Typography fontWeight={600}>{t(sla.label)}</Typography>
            </AccordionSummary>
            <AccordionDetails id={`company-sla-${sla.key}-content`}>
              <Stack gap={1} divider={<Divider flexItem />}>
                {priorityOptions.map(priority => (
                  <Stack key={priority.key} direction="row" alignItems="center" gap={2} width="100%">
                    <Typography width="18%">{t(priority.label)}</Typography>
                    <DurationInput disabled={isDisabled || prioritySLA === null} value={prioritySLA?.[priority.key][sla.key] ?? 0} onChange={value => updatePrioritySLA(priority.key, sla.key, value)} />
                  </Stack>
                ))}
              </Stack>
            </AccordionDetails>
          </Accordion>
        ))}
      </Stack>
      <Stack alignItems="flex-end"><Button variant="contained" color="success" startIcon={<SaveIcon />} onClick={() => {
        trackEventMatomo('configurações de notificações', 'click', 'botão', 'salva SLA padrão');
        savePrioritySLA(prioritySLA);
      }} disabled={isDisabled} loading={isSavingSLA}>{t('save')}</Button></Stack>

      <Divider sx={{ marginY: 4 }} />
      <Typography color="textSecondary" fontWeight="bold">Notificações de chamados</Typography>
      <Typography color="textSecondary" variant="caption">Configure os destinatários das notificações por e-mail para cada evento.</Typography>
      <Stack spacing={2} paddingTop={2}>
        {notificationEvents.map(event => (
          <Stack key={event} spacing={1}>
            <Typography fontWeight={600}>{t(NOTIFICATION_EVENT_LABELS[event])}</Typography>
            <FormGroup row>
              {notificationRecipients.map(recipient => <FormControlLabel key={recipient} label={t(NOTIFICATION_RECIPIENT_LABELS[recipient])} control={<Checkbox disabled={isDisabled} checked={ticketNotifications.events[event].includes(recipient)} onChange={change => toggleRecipient(event, recipient, change.target.checked)} />} />)}
            </FormGroup>
          </Stack>
        ))}
      </Stack>
      <Stack alignItems="flex-end" paddingTop={2}><Button variant="contained" color="success" startIcon={<SaveIcon />} onClick={() => {
        trackEventMatomo('configurações de notificações', 'click', 'botão', 'salva destinatários de notificações');
        saveTicketNotifications(ticketNotifications);
      }} disabled={isDisabled} loading={isSavingNotifications}>{t('save')}</Button></Stack>
    </Form>
  );
};

export default NotificationConfig;
